import { z } from "zod"
import { correlationIdSchema, identityIdSchema, membershipIdSchema, tenantIdSchema } from "./identifiers.js"

const canonicalUuidSchema = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/)
const siteNameSchema = z.string().regex(/^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/)

export const PORTAL_INVITATION_ASSERTION_ISSUER = "impetusos-bff-portal"
export const PORTAL_INVITATION_ASSERTION_SCOPE = "customer.portal_invitation.accept"
export const PORTAL_INVITATION_ASSERTION_MAX_LIFETIME_SECONDS = 60

export const portalInvitationAssertionClaimsV1Schema = z.object({
  version: z.literal(1),
  iss: z.literal(PORTAL_INVITATION_ASSERTION_ISSUER),
  aud: z.string(),
  site: siteNameSchema,
  tenantId: tenantIdSchema,
  scope: z.literal(PORTAL_INVITATION_ASSERTION_SCOPE),
  requestId: canonicalUuidSchema,
  correlationId: correlationIdSchema,
  jti: canonicalUuidSchema,
  invitationId: canonicalUuidSchema,
  identityId: identityIdSchema,
  emailHash: z.string().regex(/^[a-f0-9]{64}$/),
  iat: z.number().int().nonnegative().safe(),
  exp: z.number().int().positive().safe(),
}).strict().superRefine((claims, refinement) => {
  if (claims.aud !== `impetusos-frappe-portal:${claims.site}`) {
    refinement.addIssue({ code: "custom", path: ["aud"], message: "Audience must bind the site." })
  }
  if (claims.exp <= claims.iat || claims.exp - claims.iat > PORTAL_INVITATION_ASSERTION_MAX_LIFETIME_SECONDS) {
    refinement.addIssue({ code: "custom", path: ["exp"], message: "Assertion lifetime is invalid." })
  }
})

export const portalInvitationAcceptResponseV1Schema = z.object({
  responseVersion: z.literal(1),
  status: z.enum(["accepted", "denied"]),
  membershipId: membershipIdSchema.nullable(),
}).strict().superRefine((value, refinement) => {
  if ((value.status === "accepted") !== (value.membershipId !== null)) {
    refinement.addIssue({ code: "custom", path: ["membershipId"], message: "Membership result is invalid." })
  }
})

export type PortalInvitationAssertionClaimsV1 = z.infer<typeof portalInvitationAssertionClaimsV1Schema>
export type PortalInvitationAcceptResponseV1 = z.infer<typeof portalInvitationAcceptResponseV1Schema>
